import type { PropsWithChildren } from "react";
import React, { useCallback, useEffect, useState } from "react";
import type { LocaleId, MessagesMap, MsgId } from "@/messages";
import { getDefaultLocaleMessages, getLocaleMessages } from "@/messages";
import { IntlProvider, useIntl } from "react-intl";
import type { IntlFormatters } from "react-intl";
import { getDefaultLocale } from "@/lib/client/utils/locale";

type TranslationContextProps = PropsWithChildren<{
  locale: string;
}>;

export function TranslationContextProvider({ locale, children }: TranslationContextProps) {
  const [langFile, setLangFile] = useState<{
    locale: LocaleId;
    messages: MessagesMap | null;
    loadedLocale: string | null;
    dateFnsLocale: Locale | null;
  }>({
    locale: "en-US",
    messages: null,
    loadedLocale: null,
    dateFnsLocale: null,
  });

  useEffect(() => {
    let cb = async (locale: LocaleId, messages: MessagesMap, dateFnsLocale: Locale) => {
      setLangFile({
        locale,
        messages: messages || null,
        loadedLocale: locale,
        dateFnsLocale,
      });
    };

    if (locale !== langFile.loadedLocale) {
      getLocaleMessages(locale as LocaleId)
        .then(({ messages, dateFnsLocale }) => {
          console.log("Loaded locale", locale);
          cb(locale as LocaleId, messages, dateFnsLocale);
        })
        .catch(() => {
          console.log("Failed to load locale, falling back to default locale");

          cb(locale as LocaleId, getDefaultLocaleMessages(), getDefaultLocale());
        });
    }

    return () => {
      cb = async () => undefined;
    };
  }, [langFile, locale]);

  if (langFile.messages === null) {
    return <></>;
  }

  return (
    <IntlProvider locale={locale} messages={langFile.messages}>
      {children}
    </IntlProvider>
  );
}

export const useTranslations = () => {
  const formatMessage = useIntl().formatMessage;
  const locale = useIntl().locale;
  const cb = useCallback(
    (
      ...fmArgs: [MsgId] | Parameters<IntlFormatters["formatMessage"]>
    ): ReturnType<IntlFormatters["formatMessage"]> => {
      const args = (typeof fmArgs[0] === "string" ? [{ id: fmArgs[0] }] : fmArgs) as Parameters<
        IntlFormatters["formatMessage"]
      >;
      return formatMessage(...args);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatMessage, locale],
  );
  return cb;
};
